import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import { usePublishReport } from "@/profitability/report-query";
import type { ProfitabilityReport } from "@/profitability/types";
import { reportError } from "@/profitability/utils";

/** Why the draft cannot be published yet, or null when it can. */
export const publishBlocker = (
  report: ProfitabilityReport,
  answering: boolean,
) => {
  if (report.build_status === "building")
    return "Volca is building the agent. Publish once you have reviewed the result.";
  if (answering) return "Wait for Volca to finish replying.";
  if (!report.draft_version_id)
    return report.published
      ? "Nothing new to publish: ask Volca for a change and review it here first."
      : "Nothing to publish yet: the result appears here once Volca has built it.";
  if (!report.draft_sample_run_id)
    return "The draft's result is not ready yet.";
  if (report.draft_sample_run_status === "failed")
    return "The draft's result failed: ask Volca for a change, or bring back another version.";
  if (report.draft_sample_run_status !== "completed")
    return "The draft's result is still running.";
  return null;
};

/**
 * Publishes the draft the owner is looking at, named by its version, and
 * only once its result is ready and nothing is changing it: the API checks
 * the same, so a draft replaced meanwhile is refused rather than published.
 */
export const PublishDraft = ({
  businessId,
  report,
  answering,
  onPublished,
  quiet = false,
}: {
  businessId: string;
  report: ProfitabilityReport;
  answering: boolean;
  /** Called once the version is live, to take the owner to it. */
  onPublished?: () => void;
  /** The button alone, in a header: the page around it says what it waits for. */
  quiet?: boolean;
}) => {
  const publish = usePublishReport(businessId);
  const blocker = publishBlocker(report, answering);
  return (
    <Stack direction="row" gap={3} align="center" wrap>
      {blocker && !quiet && (
        <Text tone="muted" variant="caption" data-testid="publish-blocker">
          {blocker}
        </Text>
      )}
      <Button
        disabled={!!blocker || publish.isPending}
        loading={publish.isPending}
        loadingLabel="Publishing"
        onClick={() =>
          publish.mutate(
            { reportId: report.id, versionId: report.draft_version_id! },
            {
              onSuccess: () => {
                toast.success(`${report.name} is published`);
                onPublished?.();
              },
              onError: (error) =>
                toast.error("The agent could not be published", {
                  description: reportError(error),
                }),
            },
          )
        }
      >
        {report.published ? "Publish this version" : "Publish agent"}
      </Button>
    </Stack>
  );
};
